import { useCallback, useEffect, useRef } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import usePresignedUrlMutation from "features/upload/hooks/mutations/usePresignedUrlMutation";
import useMediaView from "features/common/hooks/useMediaView";
import { normalizePdfBlobIfNeeded } from "utils/pdf/normalizePdfPageSizes";

const usePDF = <T extends HTMLElement>() => {
  const pdfRef = useRef<T | null>(null);

  /**
   * @description HTML Element를 PDF로 변환합니다.
   */
  const generatePDF = async () => {
    const element = pdfRef.current;

    if (!element) return;

    // 캔버스 생성
    const canvas = await html2canvas(element, {
      allowTaint: true,
      useCORS: true, // 외부 이미지 처리
      scale: 2,
    });

    // PDF 생성
    const imageDataURL = canvas.toDataURL("image/png");

    const pdf = new jsPDF({
      orientation: "p",
      unit: "mm",
      format: "a4",
    });
    const imageWidth = 210;
    const pageHeight = 297;

    const imageHeight = (canvas.height * imageWidth) / canvas.width;
    let heightLeft = imageHeight;

    let position = 0;

    pdf.addImage(imageDataURL, "PNG", 0, position, imageWidth, imageHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imageHeight;
      pdf.addPage();
      pdf.addImage(imageDataURL, "PNG", 0, position, imageWidth, imageHeight);
      heightLeft -= pageHeight;
    }

    return pdf;
  };

  const { mutateWithFileName } = usePresignedUrlMutation();

  /**
   * @description PDF S3 업로드 함수
   */
  const uploadDocPdfAndGetS3Url =
    (pdfBlob?: Blob | null) => async (fileName: string) => {
      if (!pdfBlob) {
        throw Error("PDF 정보를 찾을 수 없습니다.");
      }

      let normalizedPdfBlob = pdfBlob;

      try {
        normalizedPdfBlob = await normalizePdfBlobIfNeeded(pdfBlob, fileName);
      } catch (error) {
        console.error("PDF 페이지 크기 정규화 실패:", error);
      }

      const file = new File([normalizedPdfBlob], fileName, {
        type: "application/pdf",
      });

      const s3Url = await mutateWithFileName(file, {
        fileType: "DOCUMENT",
        fileName: file.name,
        isOverwrite: true,
        allowedExtensions: [".pdf"],
      });

      return s3Url;
    };

  /* 모바일 리사이징 */
  const isSmallView = useMediaView("small");

  const pdfWrapRef = useRef<HTMLDivElement | null>(null);

  const handleResize = useCallback(() => {
    if (pdfWrapRef.current && pdfRef.current) {
      const scaleRatio =
        (document.documentElement.clientWidth - 32) /
        pdfRef.current.clientWidth;

      const newHeight = pdfRef.current.clientHeight * scaleRatio;

      pdfWrapRef.current.style.height = `${newHeight / 10}rem`;
      pdfRef.current.style.transform = `scale(${scaleRatio})`;
    } else {
      // 최초 렌더링 시 ref 값이 들어올 때까지 반복 실행
      setTimeout(() => handleResize(), 50);
    }
  }, []);

  const resetResize = useCallback(() => {
    if (pdfWrapRef.current && pdfRef.current) {
      pdfWrapRef.current.style.height = "unset";
      pdfRef.current.style.transform = "";
    }
  }, []);

  useEffect(() => {
    if (isSmallView) {
      window.addEventListener("resize", handleResize);
      handleResize();
    } else {
      resetResize();
    }

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize, isSmallView, resetResize]);

  return { pdfRef, pdfWrapRef, generatePDF, uploadDocPdfAndGetS3Url };
};

export default usePDF;
